import { useCallback, useContext, useId, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'

import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import RadioGroup from 'LuxKit/Radio/RadioGroup'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { isLEStaff } from 'selectors/accountSelectors'
import { isGhaPriceDisplay } from 'selectors/routerSelectors'
import { selectDateFormattingLocale } from 'lib/datetime/dateUtils'

import PropertyRefundabilityRateOption from './PropertyRefundabilityRateOption'

interface Props {
  rates: Array<App.EnrichedPropertyRate>
  selectedRateId: string | undefined
  onChange: (rateId: string) => void
  propertyTimezone: string
  /**
   * Refundability options the applied loyalty upgrade isn't offered on. They stay visible but can't be
   * chosen, so the customer can see the trade-off rather than wondering where an option went.
   */
  lockedRateIds?: Set<string>
  onRemoveUpgrade?: () => void
}

interface RefundabilityDisplayedModeOptions {
  price: number
  memberPrice: number
  canViewLuxPlusBenefits: boolean
  luxPlusEnabled: boolean
  luxPlusTourEnabled: boolean
  isGhaPricing: boolean
}

function getRefundabilityDisplayedMode({
  price,
  memberPrice,
  canViewLuxPlusBenefits,
  luxPlusEnabled,
  luxPlusTourEnabled,
  isGhaPricing,
}: RefundabilityDisplayedModeOptions): App.LuxPlusPriceStackMode {
  if (!luxPlusEnabled) {
    return 'regular-only'
  } else if (isGhaPricing && memberPrice > 0 && memberPrice < price) {
    return 'regular-prominent'
  } else if (!luxPlusTourEnabled) {
    return 'regular-only'
  } else if (!canViewLuxPlusBenefits && memberPrice > 0 && memberPrice < price) {
    return 'regular-prominent'
  } else if (canViewLuxPlusBenefits && memberPrice > 0 && memberPrice < price) {
    return 'member-prominent'
  } else {
    return 'regular-only'
  }
}

export function getDisplayedRatePrice(
  rate: App.EnrichedPropertyRate,
  displayMode: App.LuxPlusPriceStackMode,
  preferRegularPrice: boolean = false,
): number {
  return getRatePriceByDisplayMode(
    rate,
    displayMode,
    'displayPrice',
    preferRegularPrice,
  )
}

export function getSellingRatePrice(
  rate: App.EnrichedPropertyRate,
): number {
  return rate.luxPlusTotals?.sellingPrice ?? rate.totals.sellingPrice
}

function getRatePriceByDisplayMode(
  rate: App.EnrichedPropertyRate,
  displayMode: App.LuxPlusPriceStackMode,
  priceKey: 'displayPrice' | 'sellingPrice',
  preferRegularPrice: boolean = false,
): number {
  const memberPrice = rate.luxPlusTotals?.[priceKey] ?? 0
  const regularPrice = rate.totals[priceKey]

  if (preferRegularPrice) {
    return regularPrice
  }

  switch (displayMode) {
    case 'member-only':
    case 'member-prominent':
    case 'member-compact':
    case 'member-checkout-total':
      return memberPrice || regularPrice
    case 'regular-only':
    case 'regular-prominent':
    case 'regular-with-luxPlus':
    default:
      return regularPrice
  }
}

function PropertyRefundabilitySelector(props: Props) {
  const {
    rates,
    selectedRateId,
    onChange,
    propertyTimezone,
    lockedRateIds,
    onRemoveUpgrade,
  } = props

  const intl = useIntl()
  const radioGroupName = useId()
  const { currentRegionCode, locale: geoLocale } = useContext(GeoContext)
  const dateFormattingLocale = selectDateFormattingLocale(geoLocale, currentRegionCode)

  const canShowStaffLabel = useAppSelector(state => isLEStaff(state) || state.auth.account.isSpoofed)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const luxPlusTourEnabled = useAppSelector(getIsLuxPlusToursEnabled)
  const isGhaPricing = useAppSelector(isGhaPriceDisplay)

  const selectedRate = useMemo(() => {
    return rates.find(rate => rate.rateId === selectedRateId) || rates[0]
  }, [rates, selectedRateId])

  const selectedRateDisplayedMode = useMemo(() => {
    if (!selectedRate) {
      return 'regular-only'
    }

    return getRefundabilityDisplayedMode({
      price: selectedRate.totals.displayPrice,
      memberPrice: selectedRate.luxPlusTotals?.displayPrice ?? 0,
      canViewLuxPlusBenefits,
      luxPlusEnabled,
      luxPlusTourEnabled,
      isGhaPricing,
    })
  }, [selectedRate, canViewLuxPlusBenefits, luxPlusEnabled, luxPlusTourEnabled, isGhaPricing])

  const selectedRatePrice = useMemo(() => {
    if (!selectedRate) {
      return 0
    }

    return isAgentHubEnabled
      ? getDisplayedRatePrice(selectedRate, selectedRateDisplayedMode, true)
      : getSellingRatePrice(selectedRate)
  }, [selectedRate, selectedRateDisplayedMode])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const rateId = event.target.value
    onChange(rateId)
  }, [onChange])

  if (rates.length === 0) {
    return null
  }

  return (
    <RadioGroup>
      {rates.map((rate) => {
        let staffRateLabel: string | undefined = undefined
        if (canShowStaffLabel && rate.staffLabel) {
          staffRateLabel = intl.formatMessage({
            id: 'HJnIuZ3JW8_X7JmL0chU',
            defaultMessage: '{staffLabel} (label only shows for staff)',
            description: 'Staff-only label providing additional rate details',
          }, { staffLabel: rate.staffLabel })
        }
        // Match the row delta to the same displayed total price used by the selected package card.
        const rateDisplayedMode = getRefundabilityDisplayedMode({
          price: rate.totals.displayPrice,
          memberPrice: rate.luxPlusTotals?.displayPrice ?? 0,
          canViewLuxPlusBenefits,
          luxPlusEnabled,
          luxPlusTourEnabled,
          isGhaPricing,
        })
        const ratePrice = isAgentHubEnabled
          ? getDisplayedRatePrice(rate, rateDisplayedMode, true)
          : getSellingRatePrice(rate)
        const priceDifference = selectedRate && rate.rateId !== selectedRate.rateId && selectedRatePrice
          ? ratePrice - selectedRatePrice
          : 0

        return (
          <PropertyRefundabilityRateOption
            key={rate.rateId}
            rate={rate}
            radioGroupName={radioGroupName}
            isSelected={rate.rateId === selectedRateId}
            isLocked={!!lockedRateIds?.has(rate.rateId)}
            priceDifference={priceDifference}
            staffRateLabel={staffRateLabel}
            propertyTimezone={propertyTimezone}
            dateFormattingLocale={dateFormattingLocale}
            onChange={handleChange}
            onRemoveUpgrade={onRemoveUpgrade}
          />
        )
      })}
    </RadioGroup>
  )
}

export default PropertyRefundabilitySelector
